import { supabase } from '../supabase';
import { AuthError, AuthApiError } from '@supabase/supabase-js';

export const loginWithEmailAndPassword = async (
  email: string,
  password: string
) => {
  try {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    const { data } = await supabase.auth.getSession();
    return data.session;
  } catch (error) {
    console.error(error);
    throw new Error('Invalid credentials');
  }
};

export const registerWithEmailAndPassword = async (
  email: string,
  password: string
) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) throw error;

    if (!data.user) {
      throw new Error('No se pudo crear el usuario');
    }

    return data.user;
  } catch (error) {
    console.error('Error in registerWithEmailAndPassword:', error);
    if (error instanceof AuthApiError || error instanceof AuthError) {
      throw error; // Re-throw Supabase auth errors
    }
    // If it's not a Supabase error, throw a more descriptive error
    throw new Error(
      `Error durante el registro: ${
        error instanceof Error ? error.message : 'Error desconocido'
      }`
    );
  }
};

interface UserData {
  email?: string | null;
  id: string;
}

export const createUserProfile = async (user: UserData): Promise<string> => {
  try {
    if (!user.email) {
      throw new Error('User email is required');
    }
    const { data, error } = await supabase
      .from('User')
      .insert([{ email: user.email, auth_id: user.id }])
      .select('id')
      .single();
    if (error) throw error;
    return data.id;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to insert user profile');
  }
};

export const resendVerificationEmail = async (email: string) => {
  try {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
      options: {
        emailRedirectTo: 'http://localhost:3000/verify-email',
      },
    });
    if (error) throw error;
  } catch (error) {
    console.error(error);
    throw new Error('Failed to resend verification email');
  }
};

export const signInWithGoogle = async () => {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `http://localhost:3000/app/dashboard`,
    },
  });
  if (error) {
    console.error('Error signing in with Google:', error.message);
  }
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error('Error signing out:', error.message);
  }
};

export const applyToTask = async (taskId: string, userId: string) => {
  try {
    const { data, error } = await supabase
      .from('Applications')
      .insert([{ task_id: taskId, user_id: userId, status: 'pending' }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error applying to task:', error);
    throw new Error('Failed to apply to task');
  }
};

export const acceptApplication = async (applicationId: string) => {
  try {
    const { data, error } = await supabase
      .from('Applications')
      .update({ status: 'accepted' })
      .eq('id', applicationId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error accepting application:', error);
    throw new Error('Failed to accept application');
  }
};
